import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Settings as SettingsIcon, Save, Plus, Edit2, Trash2, Warehouse, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage() {
  const [warehouseSettings, setWarehouseSettings] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({});
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [warehouseRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/warehouse-settings`),
        axios.get(`${API}/suppliers`)
      ]);
      setWarehouseSettings(warehouseRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleWarehouseChange = (platform, value) => {
    setWarehouseSettings((prev) =>
      prev.map((s) =>
        s.platform === platform ? { ...s, shipping_lead_time_days: parseInt(value) || 0 } : s
      )
    );
  };

  const handleSaveWarehouses = async () => {
    if (user?.role !== 'admin') {
      toast({ title: 'Access Denied', description: 'Only admin can update settings', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/warehouse-settings`, warehouseSettings);
      toast({ title: 'Success', description: 'Warehouse settings updated' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const openSupplierDialog = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setSupplierForm({
        name: supplier.name || '',
        contact_person: supplier.contact_person || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        moq_per_color_parent: supplier.moq_per_color_parent || '',
        default_lead_time_days: supplier.default_lead_time_days || '',
        review_buffer_days: supplier.review_buffer_days || '',
        has_moq: supplier.has_moq !== false,
        min_stock_level: supplier.min_stock_level || 0
      });
    } else {
      setEditingSupplier(null);
      setSupplierForm({ 
        name: '', 
        contact_person: '', 
        email: '', 
        phone: '', 
        moq_per_color_parent: 50, 
        default_lead_time_days: 14,
        review_buffer_days: 7,
        has_moq: true,
        min_stock_level: 0
      });
    }
    setSupplierDialogOpen(true);
  };

  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, supplierForm);
        toast({ title: 'Success', description: 'Supplier updated' });
      } else {
        await axios.post(`${API}/suppliers`, supplierForm);
        toast({ title: 'Success', description: 'Supplier created' });
      }
      setSupplierDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save supplier', variant: 'destructive' });
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      toast({ title: 'Success', description: 'Supplier deleted' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete supplier', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </Layout>
    );
  }

  const platformNames = {
    amazon_uae: 'Amazon UAE',
    amazon_ksa: 'Amazon KSA',
    amazon_au: 'Amazon Australia',
    noon_uae: 'Noon.com UAE',
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight font-heading">Settings</h1>
          <p className="text-muted-foreground mt-2">Manage warehouses and suppliers</p>
        </div>

        {/* Warehouse Settings Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Warehouse className="w-5 h-5" strokeWidth={1.5} />
              <h2 className="text-2xl font-bold tracking-tight font-heading">FBA Warehouse Lead Times</h2>
            </div>
            <Button onClick={handleSaveWarehouses} disabled={saving || user?.role !== 'admin'} data-testid="save-warehouses">
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Save Changes
            </Button>
          </div>

          <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left p-4 text-xs uppercase tracking-widest font-medium">Warehouse</th>
                  <th className="text-left p-4 text-xs uppercase tracking-widest font-medium">Shipping Days</th>
                  <th className="text-left p-4 text-xs uppercase tracking-widest font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {warehouseSettings.map((setting) => (
                  <tr key={setting.platform} className="border-b border-border">
                    <td className="p-4 font-medium">{platformNames[setting.platform]}</td>
                    <td className="p-4">
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        value={setting.shipping_lead_time_days}
                        onChange={(e) => handleWarehouseChange(setting.platform, e.target.value)}
                        className="w-32 font-mono"
                        disabled={user?.role !== 'admin'}
                      />
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">From India warehouse to FBA</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Suppliers Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5" strokeWidth={1.5} />
              <h2 className="text-2xl font-bold tracking-tight font-heading">Suppliers</h2>
            </div>
            <Button onClick={() => openSupplierDialog()} disabled={user?.role !== 'admin'} data-testid="add-supplier">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Add Supplier
            </Button>
          </div>

          <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left p-4 text-xs uppercase tracking-widest font-medium">Name</th>
                  <th className="text-left p-4 text-xs uppercase tracking-widest font-medium">Contact</th>
                  <th className="text-right p-4 text-xs uppercase tracking-widest font-medium">MOQ</th>
                  <th className="text-right p-4 text-xs uppercase tracking-widest font-medium">Lead Time</th>
                  <th className="text-right p-4 text-xs uppercase tracking-widest font-medium">Min Stock</th>
                  <th className="text-center p-4 text-xs uppercase tracking-widest font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.length > 0 ? (
                  suppliers.map((supplier) => (
                    <tr key={supplier.id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-4 font-medium">{supplier.name}</td>
                      <td className="p-4 text-sm">
                        {supplier.contact_person && <div>{supplier.contact_person}</div>}
                        {supplier.email && <div className="text-muted-foreground">{supplier.email}</div>}
                      </td>
                      <td className="p-4 text-right font-mono">{supplier.has_moq === false ? 'No MOQ' : supplier.moq_per_color_parent || 50}</td>
                      <td className="p-4 text-right font-mono">{supplier.default_lead_time_days || 14} days</td>
                      <td className="p-4 text-right font-mono">{supplier.min_stock_level || 0}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => openSupplierDialog(supplier)} className="p-1 hover:bg-muted rounded-sm">
                            <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          {user?.role === 'admin' && (
                            <button onClick={() => handleDeleteSupplier(supplier.id)} className="p-1 hover:bg-destructive/10 text-destructive rounded-sm">
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="p-12 text-center text-muted-foreground">
                      No suppliers yet. Add your first supplier to track MOQs and lead times.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Supplier Dialog */}
        <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSupplierSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Supplier Name *</label>
                <Input value={supplierForm.name || ''} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Contact Person</label>
                <Input value={supplierForm.contact_person || ''} onChange={(e) => setSupplierForm({ ...supplierForm, contact_person: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <Input type="email" value={supplierForm.email || ''} onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <Input value={supplierForm.phone || ''} onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">MOQ per Color Parent</label>
                  <Input 
                    type="number" 
                    value={supplierForm.moq_per_color_parent || ''} 
                    onChange={(e) => setSupplierForm({ ...supplierForm, moq_per_color_parent: parseInt(e.target.value) || 0 })} 
                    placeholder="50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Lead Time (Days)</label>
                  <Input 
                    type="number" 
                    value={supplierForm.default_lead_time_days || ''} 
                    onChange={(e) => setSupplierForm({ ...supplierForm, default_lead_time_days: parseInt(e.target.value) || 0 })} 
                    placeholder="14"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Review Buffer (Days)</label>
                  <Input 
                    type="number" 
                    value={supplierForm.review_buffer_days || ''} 
                    onChange={(e) => setSupplierForm({ ...supplierForm, review_buffer_days: parseInt(e.target.value) || 0 })} 
                    placeholder="7"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Min Stock Level</label>
                  <Input 
                    type="number" 
                    value={supplierForm.min_stock_level || 0} 
                    onChange={(e) => setSupplierForm({ ...supplierForm, min_stock_level: parseInt(e.target.value) || 0 })} 
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="has_moq"
                  checked={supplierForm.has_moq !== false}
                  onChange={(e) => setSupplierForm({ ...supplierForm, has_moq: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="has_moq" className="text-sm">Has MOQ constraint</label>
              </div>
              <div className="flex gap-3">
                <Button type="submit">Save Supplier</Button>
                <Button type="button" variant="outline" onClick={() => setSupplierDialogOpen(false)}>Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}